import React, { useMemo, useState } from 'react';
import { ReloadOutlined } from '@ant-design/icons';
import {
	Avatar,
	Button,
	Checkbox,
	Input,
	Popconfirm,
	Segmented,
	Space,
	Table,
	Tag,
	Tooltip,
	Typography,
	message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import {
	useGetCamOptimizationsQuery,
	useSetCamOptimizedMutation,
} from '../../redux/api/manufacturing';
import type { CamOptimizationRow } from '../../interfaces/CamProgramming.interface';
import { formatOrderLineItemSubtitle } from './fields';
import { compareNullsLast, formatCamMinutes } from './camOptimizationsUtils';

const { Text } = Typography;

type OptimizedFilter = 'all' | 'not_optimized' | 'optimized';

/** Fixed column widths plus room for the part name. */
const TABLE_MIN_WIDTH = 1240;

const formatStamp = (value: string | null) =>
	value ? dayjs(value).format('MM/DD/YYYY h:mm A') : '';

const OptimizedCell: React.FC<{ record: CamOptimizationRow }> = ({ record }) => {
	const [setOptimized, { isLoading }] = useSetCamOptimizedMutation();

	const save = async (optimized: boolean) => {
		try {
			await setOptimized({ id: record.id, optimized }).unwrap();
		} catch {
			message.error('Could not save. The checkbox was reset.');
		}
	};

	const checkbox = (
		<Checkbox
			checked={record.optimized}
			disabled={isLoading}
			onChange={record.optimized ? undefined : () => save(true)}
		/>
	);

	return (
		<Space size={6} wrap>
			{record.optimized ? (
				<Popconfirm
					title="Mark as not optimized?"
					description="This clears who marked it and when."
					okText="Unmark"
					onConfirm={() => save(false)}
				>
					<Tooltip
						placement="left"
						title={`Optimized by ${record.optimized_by_name ?? 'unknown'} on ${formatStamp(record.optimized_at)}`}
					>
						{checkbox}
					</Tooltip>
				</Popconfirm>
			) : (
				checkbox
			)}
			{record.reuploaded_since_optimized && (
				<Tooltip
					title={`New CAM data uploaded ${formatStamp(record.latest_cam_upload_at)}, after it was marked optimized.`}
				>
					<Tag color="orange" style={{ margin: 0 }}>
						Re-uploaded since optimized
					</Tag>
				</Tooltip>
			)}
		</Space>
	);
};

const CamOptimizationsPanel: React.FC = () => {
	const { data, isLoading, isFetching, refetch } = useGetCamOptimizationsQuery();
	const [search, setSearch] = useState('');
	const [optimizedFilter, setOptimizedFilter] = useState<OptimizedFilter>('all');

	const rows = useMemo(() => data ?? [], [data]);

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		return rows.filter((row) => {
			if (optimizedFilter === 'optimized' && !row.optimized) return false;
			if (optimizedFilter === 'not_optimized' && row.optimized) return false;
			if (!q) return true;
			return (
				(row.name || '').toLowerCase().includes(q) ||
				(row.order_name || '').toLowerCase().includes(q)
			);
		});
	}, [rows, search, optimizedFilter]);

	const optimizedCount = rows.filter((row) => row.optimized).length;

	const columns: ColumnsType<CamOptimizationRow> = [
		{
			title: 'Part',
			key: 'part',
			render: (_, record) => (
				<Link to={`/orders/line-items/${record.id}`} className="cam-grid-part-link">
					<div className="cam-grid-part">
						{record.thumbnail_url ? (
							<Avatar shape="square" size={28} src={record.thumbnail_url} />
						) : (
							<Avatar
								shape="square"
								size={28}
								style={{ backgroundColor: '#f0f0f0', color: '#999', fontSize: 10 }}
							>
								3D
							</Avatar>
						)}
						<div className="cam-grid-part__info">
							<Text ellipsis style={{ fontSize: 13 }}>
								{record.name || '—'}
							</Text>
							<Text ellipsis type="secondary" style={{ fontSize: 11 }}>
								{formatOrderLineItemSubtitle(record)}
							</Text>
						</div>
					</div>
				</Link>
			),
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			width: 130,
		},
		{
			title: 'Qty',
			dataIndex: 'quantity',
			key: 'quantity',
			width: 70,
			align: 'right',
			sorter: (a, b) => a.quantity - b.quantity,
		},
		{
			title: 'Est. CAM run time',
			key: 'cam_minutes_total',
			width: 150,
			defaultSortOrder: 'descend',
			sortDirections: ['descend', 'ascend'],
			sorter: (a, b, sortOrder) =>
				compareNullsLast(a.cam_minutes_total, b.cam_minutes_total, sortOrder),
			render: (_, record) =>
				record.cam_minutes_total === null ? (
					<Text type="secondary">No CAM data</Text>
				) : (
					<Tooltip
						title={
							<div>
								<div>
									{formatCamMinutes(record.cam_minutes_per_part)} per part ×{' '}
									{record.quantity}
								</div>
								<div>From CAM data for {record.cam_machine_model ?? 'unknown machine model'}</div>
							</div>
						}
					>
						<Text strong>{formatCamMinutes(record.cam_minutes_total)}</Text>
					</Tooltip>
				),
		},
		{
			title: 'Per part',
			key: 'cam_minutes_per_part',
			width: 100,
			sorter: (a, b, sortOrder) =>
				compareNullsLast(a.cam_minutes_per_part, b.cam_minutes_per_part, sortOrder),
			render: (_, record) => formatCamMinutes(record.cam_minutes_per_part),
		},
		{
			title: (
				<Tooltip title="Fusion 360 CAM uploads for this line item that changed the CAM data. Edits that leave the CAM data unchanged (such as jaw configuration) are not counted.">
					Uploads
				</Tooltip>
			),
			key: 'cam_upload_count',
			width: 90,
			sorter: (a, b) => a.cam_upload_count - b.cam_upload_count,
			render: (_, record) =>
				record.cam_upload_count === 0 ? (
					<Text type="secondary">0</Text>
				) : (
					<Tag color={record.cam_upload_count >= 2 ? 'orange' : 'default'}>
						{record.cam_upload_count}
					</Tag>
				),
		},
		{
			title: 'Optimized',
			key: 'optimized',
			width: 260,
			render: (_, record) => <OptimizedCell record={record} />,
		},
		{
			title: 'Optimized by',
			key: 'optimized_by_name',
			width: 180,
			render: (_, record) =>
				record.optimized ? (
					<div>
						<div>{record.optimized_by_name ?? '—'}</div>
						<Text type="secondary" style={{ fontSize: 11 }}>
							{formatStamp(record.optimized_at)}
						</Text>
					</div>
				) : (
					<Text type="secondary">—</Text>
				),
		},
	];

	return (
		<div className="w-full bg-white border-b border-r border-l p-6">
			<Space style={{ marginBottom: 12 }} wrap size={12}>
				<Input.Search
					placeholder="Search order or part..."
					allowClear
					style={{ width: 260 }}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
				/>
				<Segmented<OptimizedFilter>
					value={optimizedFilter}
					onChange={setOptimizedFilter}
					options={[
						{ label: 'All', value: 'all' },
						{ label: 'Not optimized', value: 'not_optimized' },
						{ label: 'Optimized', value: 'optimized' },
					]}
				/>
				<Text type="secondary">
					{optimizedCount} of {rows.length} optimized
				</Text>
				<Button
					icon={<ReloadOutlined />}
					onClick={() => refetch()}
					loading={isFetching && !isLoading}
				>
					Refresh
				</Button>
			</Space>
			<Table<CamOptimizationRow>
				rowKey="id"
				size="small"
				loading={isLoading || isFetching}
				columns={columns}
				dataSource={filtered}
				scroll={{ x: TABLE_MIN_WIDTH }}
				pagination={{ pageSize: 50, showSizeChanger: true }}
			/>
		</div>
	);
};

export default CamOptimizationsPanel;
